import { matchPath } from 'react-router-dom'
import { ROUTE_PATHS, ROUTE_PATTERNS } from '@/config/routes'

/** In-app breadcrumb crumb (`to` is a react-router path). */
export type BreadcrumbItem = {
  label: string
  to?: string
}

const SEGMENT_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  applicant: 'My application',
  company: 'Companies',
  companies: 'Companies',
  property: 'Properties',
  properties: 'Properties',
  invite: 'Invites',
  invitee: 'Invites',
  yardiinvite: 'Yardi invite',
  'yardi-invite': 'Yardi invite',
  'user-management': 'User management',
  product: 'Products',
  products: 'Products',
  'email-templates': 'Email templates',
  application: 'Application',
  applications: 'Applications',
  resident: 'Residents',
  residents: 'Residents',
  defaults: 'Reimbursements',
  reimbursements: 'Reimbursements',
  'resident-detail': 'Resident detail',
  'default-detail': 'Reimbursement detail',
  reports: 'Reports',
  'reports-scheduler': 'Reports scheduler',
  mailtrap: 'Mailtrap Logs',
  transfer: 'Transfer',
  'transfer-list': 'Transfer list',
  'import-data': 'Import data',
  'sales-team': 'Sales team',
  'property-maintenance': 'Property maintenance',
  'help-about': 'Help & about',
  help: 'Help',
  account: 'Account',
  messages: 'Messages',
  'resident-application': 'Resident application',
  add: 'Add',
  edit: 'Edit',
  search: 'Search',
  about: 'About',
  income: 'Income',
  history: 'History',
  signature: 'Signature',
  payment: 'Payment',
  terms_info: 'Terms & notices',
  'payment-logs': 'Revenue Payment Logs',
  fair_housing_act: 'Fair housing act',
  credit_reporting_bureau: 'Credit reporting bureau',
  authorization_for_liberty: 'Authorization for Liberty',
  roles: 'Roles',
  'user-roles': 'Roles',
  version: 'Whats New',
  stock: 'Stock registry',
  'underwriting-config': 'Underwriting config',
  'terms-and-conditions': 'Terms & conditions',
  'gdpr-consent': 'GDPR',
  'gdpr-document': 'Service agreement',
  'screening-info-requests': 'Screening info requests',
  'frequently-used-notes': 'Reimbursement notes',
  // Report list segments under /reports/*
  applicationInvite: 'Active Invites Status',
  MRG_Report: 'MRG Report',
  'pmc-performance-red': 'PMC Performance Report - Red',
  'pmc-performance-green': 'PMC Performance Report - Green',
  balances_report: 'Balance Report (Outstanding/Adjustment)',
  'default-payment-logs': 'Default Payment Logs',
  'mailgun-logs': 'Mailgun Logs',
  'mail-logs': 'Mail Logs (legacy)',
  'pending-defaults': 'Pending Reimbursements Report',
  chargeback: 'Chargeback Report',
  'total-number-of-contract': 'Total Number Of Contracts',
  'api-logs': 'API Logs',
  'financial-mrg': 'Weekly Reimbursements Report',
  'message-logs': 'SMS Message Logs',
  'impersonate-logs': 'Impersonate Logs',
  'financial-daily': 'Financial Daily Report',
  'financial-daily-defaults': 'Financial Daily Report - Defaults Paid',
  'non-verified-property': 'Non-verified Property Report',
  'claim-not-timely': 'Reimbursement Not Opened Timely',
  'expire-defaults': 'Auto Expire Reimbursements Report',
  'active-properties-no-application': 'Active Properties No Application Report',
  'missing-contacts': 'Missing Contacts Report',
  'imap-logs': 'IMAP Inbox',
  'mobile-event-log': 'Mobile Event Log',
  'failed-logins': 'Failed Logins Log',
  'daily-application': 'Daily Application Report',
  'contract-loss-ratio': 'Contract Loss Ratio',
  'mrg-loss-ratio': 'MRG Loss Ratio',
  'w9-uploaded': 'W9 Uploaded Report',
  'organic-growth': 'Organic Growth Report',
  'approved-not-purchased': 'Approved Not Purchased Report',
  'application-completed-fee-not-paid': 'Application Completed Fee Not Paid',
  'property-activity': 'Property Activity Report',
  'lr-form-16': 'LR Form 16',
  'dynamic-pricing': 'Dynamic Pricing Report',
  detail: 'Details',
  read: 'Details',
}

function titleCaseSegment(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment]
  if (/^[\d-]+$/.test(segment) || /^[0-9a-f-]{8,}/i.test(segment)) {
    return '…'
  }
  return segment
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * True for numeric IDs, UUIDs, or long opaque tokens.
 * Must not treat kebab-case path segments (e.g. pmc-performance-red) as IDs —
 * those used to hit "length > 18" and became the breadcrumb "Details".
 */
function isLikelyId(segment: string): boolean {
  if (/^\d+$/.test(segment)) return true
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      segment,
    )
  ) {
    return true
  }
  // Long single-token slug (no hyphens/underscores) — not multi-word route names
  if (segment.length > 20 && /^[a-z0-9]+$/i.test(segment)) return true
  return false
}

type PatternSpec = {
  path: string
  build: (pageTitle: string | null) => BreadcrumbItem[]
}

const DASHBOARD_PREFIX: BreadcrumbItem[] = [
  { label: 'Dashboard', to: ROUTE_PATHS.DASHBOARD },
]

const PATTERN_SPECS: PatternSpec[] = [
  {
    path: ROUTE_PATTERNS.DASHBOARD_DOCUMENT_TERMS_EDIT,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Terms', to: ROUTE_PATHS.DASHBOARD_DOCUMENT_TERMS },
      { label: 'Edit terms & conditions' },
    ],
  },
  {
    path: ROUTE_PATHS.DASHBOARD_DOCUMENT_TERMS,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Terms' },
    ],
  },
  {
    path: ROUTE_PATHS.DASHBOARD_DOCUMENT_SERVICE,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Service agreement' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_DOCUMENT_SERVICE_EDIT,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Service agreement', to: ROUTE_PATHS.DASHBOARD_DOCUMENT_SERVICE },
      { label: 'Edit service agreement' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_DOCUMENT_SERVICE_VIEW,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Service agreement', to: ROUTE_PATHS.DASHBOARD_DOCUMENT_SERVICE },
      { label: 'View service agreement' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_COMPANY_EDIT,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Companies', to: ROUTE_PATHS.DASHBOARD_COMPANIES },
      { label: pageTitle || 'Edit company' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_PROPERTY_EDIT,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Properties', to: ROUTE_PATHS.DASHBOARD_PROPERTIES },
      { label: pageTitle || 'Edit property' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_INVITEE_EDIT,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Invites', to: ROUTE_PATHS.DASHBOARD_INVITEE },
      { label: pageTitle || 'Edit invite' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_INVITEE_ADD,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Invites', to: ROUTE_PATHS.DASHBOARD_INVITEE },
      { label: 'Add invite' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_YARDI_INVITE_SEARCH,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Yardi invite', to: ROUTE_PATHS.DASHBOARD_YARDI_INVITE },
      { label: 'Search/ Invite Yardi guest cards' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_COMPANY_ADD,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Companies', to: ROUTE_PATHS.DASHBOARD_COMPANIES },
      { label: 'Add company' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_PROPERTY_ADD,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Properties', to: ROUTE_PATHS.DASHBOARD_PROPERTIES },
      { label: 'Add property' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_USER_MANAGEMENT_EDIT,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'User management', to: ROUTE_PATHS.DASHBOARD_USER_MANAGEMENT },
      { label: pageTitle || 'Edit user' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_USER_MANAGEMENT_ROLES,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'User management', to: ROUTE_PATHS.DASHBOARD_USER_MANAGEMENT },
      { label: pageTitle || 'Assign roles' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_USER_MANAGEMENT_ADD,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'User management', to: ROUTE_PATHS.DASHBOARD_USER_MANAGEMENT },
      { label: 'Add user' },
    ],
  },
  {
    // `/user/profile` — there is no `/user` route, so never emit a "User" parent crumb.
    path: ROUTE_PATHS.DASHBOARD_PROFILE,
    build: () => [...DASHBOARD_PREFIX, { label: 'Profile' }],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_PRODUCT_EDIT,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Products', to: ROUTE_PATHS.DASHBOARD_PRODUCTS },
      { label: pageTitle || 'Edit product' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_PRODUCT_ADD,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Products', to: ROUTE_PATHS.DASHBOARD_PRODUCTS },
      { label: 'Add product' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_EMAIL_TEMPLATES_EDIT,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Email templates', to: ROUTE_PATHS.DASHBOARD_EMAIL_TEMPLATES },
      { label: pageTitle || 'Edit email template' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_EMAIL_TEMPLATES_ADD,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Email templates', to: ROUTE_PATHS.DASHBOARD_EMAIL_TEMPLATES },
      { label: 'Add email template' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_APPLICATION_DETAIL,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Applications', to: ROUTE_PATHS.DASHBOARD_APPLICATIONS },
      { label: pageTitle || 'Application details' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_RESIDENT_DETAIL,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Residents', to: ROUTE_PATHS.DASHBOARD_RESIDENTS },
      { label: pageTitle || 'Resident detail' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DEFAULT_DETAIL_WITH_REIMBURSEMENT,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Reimbursements', to: ROUTE_PATHS.DEFAULTS },
      { label: pageTitle || 'Reimbursement detail' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DEFAULT_DETAIL,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Reimbursements', to: ROUTE_PATHS.DEFAULTS },
      { label: pageTitle || 'Reimbursement detail' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_TRANSFER_DETAIL_WITH_COMPANY,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Transfer' },
    ],
  },
  {
    path: ROUTE_PATTERNS.DASHBOARD_TRANSFER_DETAIL,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Transfer' },
    ],
  },
  {
    path: ROUTE_PATHS.DASHBOARD_REPORTS,
    build: () => [...DASHBOARD_PREFIX, { label: 'All reports' }],
  },
  {
    // Mailtrap lives at /mailtrap (not under /reports) but is an All Reports entry
    path: ROUTE_PATHS.DASHBOARD_MAILTRAP,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Reports', to: ROUTE_PATHS.DASHBOARD_REPORTS },
      { label: 'Mailtrap Logs' },
    ],
  },
  {
    path: ROUTE_PATHS.DASHBOARD_REPORTS_DEFAULT_PAYMENT_LOGS,
    build: () => [
      ...DASHBOARD_PREFIX,
      { label: 'Reports', to: ROUTE_PATHS.DASHBOARD_REPORTS },
      { label: 'Default Payment Logs' },
    ],
  },
  {
    // Revenue Payment Logs detail: `/dashboard/reports/payment-logs/:logId`
    path: ROUTE_PATTERNS.DASHBOARD_REPORT_PAYMENT_LOG_DETAIL,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Reports', to: ROUTE_PATHS.DASHBOARD_REPORTS },
      {
        label: 'Revenue Payment Logs',
        to: ROUTE_PATHS.DASHBOARD_REPORTS_PAYMENT_LOGS,
      },
      { label: pageTitle || 'Transaction Details' },
    ],
  },
  {
    // Default Payment Logs detail: `/dashboard/reports/default-payment-logs/:logId`
    path: ROUTE_PATTERNS.DASHBOARD_REPORT_DEFAULT_PAYMENT_LOG_DETAIL,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Reports', to: ROUTE_PATHS.DASHBOARD_REPORTS },
      {
        label: 'Default Payment Logs',
        to: ROUTE_PATHS.DASHBOARD_REPORTS_DEFAULT_PAYMENT_LOGS,
      },
      { label: pageTitle || 'Transaction Details' },
    ],
  },
  {
    // Chargeback detail: `/reports/chargeback/detail/:applicantId/:logId`
    path: ROUTE_PATTERNS.DASHBOARD_REPORT_CHARGEBACK_DETAIL,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Reports', to: ROUTE_PATHS.DASHBOARD_REPORTS },
      {
        label: 'Chargeback Report',
        to: ROUTE_PATHS.DASHBOARD_REPORTS_CHARGEBACK,
      },
      { label: pageTitle || 'Chargeback Details' },
    ],
  },
  {
    // Payment adjustment detail from revenue logs: `/dashboard/reports/payment-logs/detail/:logId`
    path: ROUTE_PATTERNS.DASHBOARD_REPORT_PAYMENT_LOG_ADJUSTMENT_DETAIL,
    build: (pageTitle) => [
      ...DASHBOARD_PREFIX,
      { label: 'Reports', to: ROUTE_PATHS.DASHBOARD_REPORTS },
      {
        label: 'Revenue Payment Logs',
        to: ROUTE_PATHS.DASHBOARD_REPORTS_PAYMENT_LOGS,
      },
      { label: pageTitle || 'Transaction Details' },
    ],
  },
  /* Jobs: URL has `/jobs/queued` but there is no `/jobs` index — skip the `jobs` segment. */
  {
    path: ROUTE_PATHS.DASHBOARD_JOBS_QUEUED,
    build: () => [...DASHBOARD_PREFIX, { label: 'Queued jobs' }],
  },
  {
    path: ROUTE_PATHS.DASHBOARD_JOBS_FAILED,
    build: () => [...DASHBOARD_PREFIX, { label: 'Failed jobs' }],
  },
  {
    path: ROUTE_PATHS.DASHBOARD_JOBS_CRON,
    build: () => [...DASHBOARD_PREFIX, { label: 'Cron jobs' }],
  },
  {
    path: ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR,
    build: () => [...DASHBOARD_PREFIX, { label: 'Cron monitoring' }],
  },
]

const APPLICANT_PREFIX: BreadcrumbItem[] = [
  { label: 'My application', to: ROUTE_PATHS.APPLICANT_DASHBOARD },
]

const SALES_PATTERN: PatternSpec = {
  path: ROUTE_PATHS.SALES_TEAM,
  build: () => [
    { label: 'Dashboard', to: ROUTE_PATHS.DASHBOARD },
    { label: 'Sales team' },
  ],
}

function findApplicantAppTab(
  pathname: string,
  pageTitle: string | null,
): BreadcrumbItem[] | null {
  const m = matchPath({ path: ROUTE_PATTERNS.APPLICANT_APPLICATION_TAB, end: true }, pathname)
  if (!m) return null
  const tab = m.params.tab
  const tabLabel =
    (tab && SEGMENT_LABELS[tab]) || (tab ? titleCaseSegment(String(tab)) : 'Application')
  return [
    ...APPLICANT_PREFIX,
    { label: 'Current application', to: ROUTE_PATHS.APPLICANT_APPLICATION_ABOUT },
    { label: pageTitle || tabLabel },
  ]
}

function buildDashboardCrumbs(
  parts: string[],
  pageTitle: string | null,
): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [{ label: 'Dashboard', to: ROUTE_PATHS.DASHBOARD }]
  let acc: string = ROUTE_PATHS.DASHBOARD
  for (let i = 1; i < parts.length; i++) {
    const seg = parts[i]!
    acc = `${acc}/${seg}`
    const isLast = i === parts.length - 1
    const prev = parts[i - 1]!
    const next = parts[i + 1]
    // `/…/read/:id`, `/…/detail/:id`, or `/…/edit/:id` — omit useless middle crumbs
    if (
      !isLast &&
      (seg === 'read' || seg === 'detail' || seg === 'edit') &&
      next &&
      isLikelyId(next)
    ) {
      continue
    }
    const showPageTitle =
      isLast && pageTitle && (isLikelyId(seg) || prev === 'edit' || prev === 'roles' || prev === 'read' || prev === 'detail')
    // Prefer explicit segment labels; avoid falling back to "Details" for long route slugs
    const label = showPageTitle
      ? pageTitle
      : SEGMENT_LABELS[seg] ||
        (isLikelyId(seg) ? pageTitle || 'Details' : titleCaseSegment(seg))

    // `/…/edit/:id` — never link bare `/edit` (no matching route → 404).
    const isEditOrAddBeforeId =
      !isLast && (seg === 'edit' || seg === 'add') && !!next && isLikelyId(next)
    // ID segment in the middle of a path — link to parent list instead.
    const isMiddleId = !isLast && isLikelyId(seg)

    if (isLast || isEditOrAddBeforeId || isMiddleId) {
      items.push({ label })
    } else {
      items.push({ label, to: acc })
    }
  }
  return items
}

function buildApplicantCrumbs(
  parts: string[],
  pageTitle: string | null,
): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [
    { label: 'My application', to: ROUTE_PATHS.APPLICANT_DASHBOARD },
  ]
  let acc: string = ROUTE_PATHS.APPLICANT
  for (let i = 1; i < parts.length; i++) {
    const seg = parts[i]!
    acc = `${acc}/${seg}`
    const isLast = i === parts.length - 1
    if (seg === 'dashboard' && i === 1) {
      if (isLast) {
        items.push({ label: 'Home' })
      } else {
        items.push({ label: 'Home', to: acc })
      }
      continue
    }
    if (seg === 'application' && i === 1) {
      if (parts[i + 1] && !isLast) {
        items.push({ label: 'Current application', to: ROUTE_PATHS.APPLICANT_APPLICATION_ABOUT })
      }
      continue
    }
    const label = SEGMENT_LABELS[seg] || titleCaseSegment(seg)
    if (isLast) {
      items.push({ label: pageTitle || label })
    } else {
      items.push({ label, to: acc })
    }
  }
  return items
}

function buildFallbackCrumbs(
  parts: string[],
  pageTitle: string | null,
): BreadcrumbItem[] {
  if (parts.length === 0) {
    return [{ label: 'Home' }]
  }
  const out: BreadcrumbItem[] = []
  let acc = ''
  for (let i = 0; i < parts.length; i++) {
    const seg = parts[i]!
    acc = i === 0 ? `/${seg}` : `${acc}/${seg}`
    const isLast = i === parts.length - 1
    const prev = i > 0 ? parts[i - 1]! : ''
    const next = parts[i + 1]
    // `/…/edit/:id`, `/…/detail/:id`, `/…/read/:id` — omit useless middle crumbs
    // (e.g. Default Payment Log edit: Dashboard > Reports > Default Payment Logs > Edit Default Payment Log)
    if (
      !isLast &&
      (seg === 'read' || seg === 'detail' || seg === 'edit') &&
      next &&
      isLikelyId(next)
    ) {
      continue
    }
    const useOverride =
      isLast &&
      pageTitle &&
      (isLikelyId(seg) ||
        prev === 'edit' ||
        prev === 'payment-logs' ||
        prev === 'detail' ||
        prev === 'read')
    const label = useOverride
      ? pageTitle
      : (SEGMENT_LABELS[seg] || titleCaseSegment(seg))
    // Never link bare `/edit` or `/detail` (no list route → 404).
    const isActionBeforeId =
      !isLast &&
      (seg === 'edit' || seg === 'add' || seg === 'detail' || seg === 'read') &&
      !!next &&
      isLikelyId(next)
    const isMiddleId = !isLast && isLikelyId(seg)
    if (isLast || isActionBeforeId || isMiddleId) {
      out.push({ label })
    } else {
      out.push({ label, to: acc })
    }
  }
  return out
}

/**
 * Breadcrumb items for the main dashboard shell (`BaseDashboardLayout`).
 * `pageTitle` is the optional override for the last segment (e.g. entity name on edit/detail pages).
 */
export function getDashboardBreadcrumbItems(
  pathname: string,
  pageTitle: string | null,
): BreadcrumbItem[] {
  const normalized = pathname.replace(/\/$/, '') || ROUTE_PATHS.ANGULAR_HOME

  for (const spec of PATTERN_SPECS) {
    if (matchPath({ path: spec.path, end: true }, normalized)) {
      return spec.build(pageTitle)
    }
  }

  if (matchPath({ path: SALES_PATTERN.path, end: true }, normalized)) {
    return SALES_PATTERN.build(pageTitle)
  }

  if (matchPath({ path: ROUTE_PATTERNS.RESIDENT_APPLICATION_DETAIL, end: true }, normalized)) {
    return [
      { label: 'My application', to: ROUTE_PATHS.APPLICANT_DASHBOARD },
      { label: pageTitle || 'Application' },
    ]
  }

  const appTab = findApplicantAppTab(normalized, pageTitle)
  if (appTab) {
    return appTab
  }

  if (normalized === ROUTE_PATHS.DASHBOARD) {
    return [{ label: 'Dashboard' }]
  }
  if (normalized === ROUTE_PATHS.APPLICANT || normalized === ROUTE_PATHS.APPLICANT_DASHBOARD) {
    return [{ label: 'My application' }]
  }

  const parts = normalized.split(ROUTE_PATHS.ANGULAR_HOME).filter(Boolean)
  if (parts.length === 0) {
    return [{ label: 'Home' }]
  }

  const root = parts[0]
  if (root === 'dashboard') {
    if (parts.length === 1) {
      return [{ label: 'Dashboard' }]
    }
    return buildDashboardCrumbs(parts, pageTitle)
  }
  if (root === 'applicant') {
    return buildApplicantCrumbs(parts, pageTitle)
  }
  // Admin routes at root (Angular LayoutComponent parity — no `/dashboard` prefix)
  return [
    { label: 'Dashboard', to: ROUTE_PATHS.DASHBOARD },
    ...buildFallbackCrumbs(parts, pageTitle),
  ]
}
